import { FC, useMemo } from 'react';
import { SchedulingOutcomeAggregate } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { callIntelKpiUtils, generateTrackingId } from '@frontend/call-intel-core';
import { Trans, useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Text } from '@frontend/design-system';
import { useCallIntelDemoFlags } from '../../../hooks/use-call-intel-demo-flags';
import { useNavigateToCallRecordsWithFilter } from '../../../hooks/use-navigate-to-call-records-with-filter';
import { SchedulingStatsCard } from '../scheduling-stats-card';
import { StatItem } from '../scheduling-stats-card/stats-list';

const COMPONENT_TRACKING_ID = 'conversion-during-call-card';

export const ConversionDuringCallCard: FC<{ schedulingAggregateStats?: SchedulingOutcomeAggregate }> = ({
  schedulingAggregateStats,
}) => {
  const { t } = useTranslation('call-intelligence');
  const { isDemoAccount } = useCallIntelDemoFlags();
  const { patient: patientToken, patientCapitalized: patientCapitalizedToken } = useTranslationTokens();

  const { navigateToCallRecords } = useNavigateToCallRecordsWithFilter();

  const { scheduledOnCallCount, scheduledRate, unscheduledOnCall, scheduledOpportunitiesCount } = useMemo(() => {
    const scheduledOpportunitiesCount = schedulingAggregateStats?.scheduledOpportunitiesCount ?? 0;
    const scheduledOnCallCount = schedulingAggregateStats?.scheduledOnCallCount ?? 0;
    const scheduledPostCallCount = schedulingAggregateStats?.scheduledPostCallCount ?? 0;

    const scheduledRate = callIntelKpiUtils.formatPercentWithPrecision(
      (scheduledOnCallCount / scheduledOpportunitiesCount) * 100 || 0
    );
    return {
      scheduledPostCallCount,
      scheduledOnCallCount,
      scheduledOpportunitiesCount,
      unscheduledOnCall: scheduledOpportunitiesCount - scheduledOnCallCount,
      scheduledRate,
    };
  }, [schedulingAggregateStats]);

  const statsListItems = useMemo<StatItem[]>(() => {
    return [
      {
        label: t('Scheduled Rate'),
        value: `${scheduledRate}%`,
        trackingId: generateTrackingId({
          component: COMPONENT_TRACKING_ID,
          context: 'scheduled-rate',
          isDemoAccount,
        }),
        tooltip: t('Indicates your scheduling performance during the call'),
        dataTestId: 'scheduled-rate-stat',
      },
      {
        label: t('Scheduled On Call'),
        value: scheduledOnCallCount,
        trackingId: generateTrackingId({
          component: COMPONENT_TRACKING_ID,
          context: 'scheduled-on-call',
          isDemoAccount,
        }),
        onClick: () =>
          navigateToCallRecords({
            schedulingOutcomeV2s: [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL],
          }),
        tooltip: t('{{patientCapitalizedToken}} scheduled an appointment during the call', {
          patientCapitalizedToken,
        }),
        dataTestId: 'scheduled-on-call-stat',
      },
      {
        label: t('Unscheduled On Call'),
        value: unscheduledOnCall,
        trackingId: generateTrackingId({
          component: COMPONENT_TRACKING_ID,
          context: 'unscheduled-on-call',
          isDemoAccount,
        }),
        onClick: () =>
          navigateToCallRecords({
            schedulingOutcomeV2s: [
              CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED,
              CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL,
            ],
          }),
        tooltip: t('{{patientCapitalizedToken}} did not schedule an appointment during the call', {
          patientCapitalizedToken,
        }),
        dataTestId: 'unscheduled-on-call-stat',
      },
    ];
  }, [
    isDemoAccount,
    navigateToCallRecords,
    scheduledRate,
    unscheduledOnCall,
    scheduledOnCallCount,
    t,
    patientCapitalizedToken,
  ]);

  return (
    <SchedulingStatsCard dataTestId='conversion-during-call-card'>
      <SchedulingStatsCard.Header
        title={t('Conversion During Call')}
        subtitle={
          <Text size='large' as='p' data-testid='conversion-during-call-subtitle'>
            <Trans t={t} count={scheduledOpportunitiesCount} values={{ scheduledOpportunitiesCount }}>
              Out of <strong>{{ scheduledOpportunitiesCount }}</strong> Opportunities
            </Trans>
          </Text>
        }
        callIntelTooltipId='conversionDuringCall'
      />
      <SchedulingStatsCard.List items={statsListItems} />
      <SchedulingStatsCard.Footer
        text={t('Tracks calls where the {{patientToken}} successfully scheduled during the conversation.', {
          patientToken,
        })}
      />
    </SchedulingStatsCard>
  );
};
